import { useIntegrationContent } from '@common/hooks/useIntegrationContent';
import { IntegrationContentNotice } from '@common/components/IntegrationContentNotice';
import { type RefObject, useEffect, useRef, useState } from "react";
import { Info, Question, ShieldCheck } from "@phosphor-icons/react";
import { useTranslation } from "@client-app/i18n";
import {
  getConnectionPrivacyPolicyUrl,
  getIntegrationDisclosure,
} from "@common/lib/integration-disclosure";
import Modal from "./Settings/components/Modal";
import { getIntegrationProductCopy } from "./Settings/components/integrations/productCopy";

type DetailView = "scope" | "privacy" | "revocation";
type DisclosureView = "overview" | DetailView;
interface DisclosureProps {
  tool: string;
  isPlugin?: boolean;
  content?: ReturnType<typeof useIntegrationContent>;
}
const key = (name: string) => `website.connectionDisclosure.${name}`;
const linkClass =
  "rounded-lg text-sm font-medium text-[#1A162F] underline underline-offset-4 hover:text-[#666F8D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/30";
const buttonClass =
  "rounded-[10px] px-4 py-2.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/30";

function DisclosureOverview({
  tool,
  isPlugin = false,
  content,
  onView,
}: DisclosureProps & {
  onView: (view: DetailView) => void;
}) {
  const { t, locale } = useTranslation();
  const loaded = useIntegrationContent(content === undefined);
  const current = content || loaded;
  const disclosure = getIntegrationDisclosure(tool, current.rows, locale);
  if (current.status !== 'ready' || !disclosure) {
    return <IntegrationContentNotice status={current.status === 'ready' ? 'missing' : current.status}
      zh={locale === 'zh-CN'} onRetry={current.retry} />;
  }
  const copy = (name: string) =>
    getIntegrationProductCopy(t(key(name)), isPlugin);
  const capabilities = disclosure;
  const isDynamic = disclosure.capabilitySource === "discovered";
  return (
    <div
      data-integration-disclosure={tool}
      className="space-y-6 text-sm leading-6 text-[#666F8D]"
    >
      <section className="space-y-2">
        <h3 className="font-medium text-[#1A162F]">{copy("purpose")}</h3>
        <p>{getIntegrationProductCopy(disclosure.purpose, isPlugin)}</p>
      </section>
      {isDynamic ? (
        <p data-capability-source="discovered">{copy("dynamic")}</p>
      ) : (
        <>
          <section className="space-y-2">
            <h3 className="font-medium text-[#1A162F]">{copy("read")}</h3>
            {capabilities.read.length ? (
              <ul className="list-disc space-y-1 pl-5">
                {capabilities.read.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <p>{copy("unavailable")}</p>
            )}
          </section>
          <section className="space-y-2">
            <h3 className="font-medium text-[#1A162F]">{copy("write")}</h3>
            {capabilities.write.length ? (
              <ul className="list-disc space-y-1 pl-5">
                {capabilities.write.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <p>{capabilities.read.length ? t("website.integrationsSettings.readOnly") : copy("noWrite")}</p>
            )}
          </section>
        </>
      )}
      <div className="grid grid-cols-1 gap-2.5 border-t border-[#E8EBF3] pt-4 sm:grid-cols-3">
        {([
          { view: "scope", title: "scopeTitle", Icon: Info },
          { view: "revocation", title: "revocation", Icon: Question },
          { view: "privacy", title: "privacy", Icon: ShieldCheck },
        ] as const).map(({ view, title, Icon }) => (
          <button
            key={view}
            type="button"
            aria-label={copy(title)}
            aria-haspopup="dialog"
            data-disclosure-card={view}
            className="flex items-center gap-2 rounded-lg border border-[#E8EBF3] bg-white px-3 py-2 text-left transition-[border-color] duration-150 hover:border-[#C8D0E2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/30"
            onClick={() => onView(view)}
          >
            <Icon aria-hidden="true" className="shrink-0 text-[#666F8D]" size={16} />
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-[#1A162F]">
              {copy(title)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ConnectionInformation({
  view,
  isPlugin = false,
}: {
  view: DetailView;
  isPlugin?: boolean;
}) {
  const { t } = useTranslation();
  const copy = (name: string) =>
    getIntegrationProductCopy(t(key(name)), isPlugin);
  const sections =
    view === "scope"
      ? [["scopeTitle", "scope"]]
      : view === "revocation"
        ? [
            ["disconnectTitle", "disconnect"],
            ["providerTitle", "provider"],
          ]
        : [
            ["privacyDataTitle", "privacyData"],
            ["privacyCredentialsTitle", "privacyCredentials"],
          ];
  return (
    <div className="space-y-6 text-sm leading-6 text-[#666F8D]">
      {sections.map(([title, body]) => (
        <section key={title} className="space-y-2">
          {view !== "scope" ? (
            <h3 className="font-medium text-[#1A162F]">{copy(title)}</h3>
          ) : null}
          <p>{copy(body)}</p>
        </section>
      ))}
      <section className="space-y-3 rounded-xl border border-[#E8EBF3] bg-[#F8F9FC] p-4">
        <p>{copy(view === "revocation" ? "retention" : "privacyPolicyText")}</p>
        <a
          className={linkClass}
          href={getConnectionPrivacyPolicyUrl(isPlugin)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {copy("privacyPolicy")}
        </a>
      </section>
    </div>
  );
}

function useDisclosureKeyboard(
  bodyRef: RefObject<HTMLDivElement>,
  onClose: () => void,
  exitRef?: RefObject<HTMLButtonElement>,
) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const dialog = bodyRef.current?.closest('[role="dialog"]');
    const initialFocus = exitRef?.current ?? dialog?.querySelector<HTMLElement>("header button");
    initialFocus?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const controls = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]',
        ),
      );
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", handleKey, true);
    return () => {
      document.removeEventListener("keydown", handleKey, true);
      previousFocus?.focus();
    };
  }, [bodyRef, exitRef]);
}

/** Existing Admin Modal, with focus containment, Escape and focus restoration. */
export function IntegrationDisclosureModal({
  tool,
  isPlugin = false,
  initialView = "overview",
  content,
  onClose,
  onContinue,
}: Partial<DisclosureProps> & {
  initialView?: DisclosureView;
  onClose: () => void;
  onContinue?: () => void;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<DisclosureView>(initialView);
  const bodyRef = useRef<HTMLDivElement>(null);
  const exitRef = useRef<HTMLButtonElement>(null);
  const continuedRef = useRef(false);
  const copy = (name: string) =>
    getIntegrationProductCopy(t(key(name), { tool: tool || "" }), isPlugin);
  useDisclosureKeyboard(bodyRef, onClose, exitRef);
  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
    exitRef.current?.focus();
  }, [view]);
  const showBack =
    view !== "overview" && Boolean(tool) && initialView === "overview";
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={copy(
        view === "overview"
          ? "title"
          : view === "scope"
            ? "scopeTitle"
            : view === "privacy"
              ? "privacyTitle"
              : "revocation",
      )}
      closeLabel={copy("close")}
      width="max-w-2xl"
      className="max-h-[85vh]"
      noPadding
      contentScrollable={false}
      contentClassName="flex min-h-0 flex-col"
      showHeaderDivider
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div ref={bodyRef} className="custom-scrollbar min-h-0 overflow-y-auto px-6 py-5 [scrollbar-gutter:stable]">
          {view === "overview" && tool ? (
            <DisclosureOverview
              tool={tool}
              isPlugin={isPlugin}
              content={content}
              onView={setView}
            />
          ) : (
            <ConnectionInformation
              view={view === "overview" ? "privacy" : view}
              isPlugin={isPlugin}
            />
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-3 border-t border-[#E8EBF3] bg-white px-6 py-4">
          <button
            ref={exitRef}
            type="button"
            className={buttonClass + " text-[#666F8D] hover:bg-[#F4F5F8]"}
            onClick={() => (showBack ? setView("overview") : onClose())}
          >
            {copy(showBack ? "back" : onContinue ? "cancel" : "close")}
          </button>
          {view === "overview" && onContinue ? (
            <button
              type="button"
              className={
                buttonClass + " bg-[#1A162F] text-white hover:bg-[#241F3D]"
              }
              onClick={() => {
                if (continuedRef.current) return;
                continuedRef.current = true;
                onContinue();
              }}
            >
              {copy("continue")}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export default function IntegrationDisclosure(props: DisclosureProps) {
  const [view, setView] = useState<DetailView | null>(null);
  return (
    <>
      <DisclosureOverview {...props} onView={setView} />
      {view ? (
        <IntegrationDisclosureModal
          {...props}
          initialView={view}
          onClose={() => setView(null)}
        />
      ) : null}
    </>
  );
}

export function ConnectionPrivacyButton({
  isPlugin = false,
}: {
  isPlugin?: boolean;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={linkClass}
        onClick={() => setIsOpen(true)}
      >
        {t(key("privacy"))}
      </button>
      {isOpen ? (
        <IntegrationDisclosureModal
          isPlugin={isPlugin}
          initialView="privacy"
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
}
